import { useState } from "react";
import LogoTray from "../LogoTray";
import "../../Search.css";
import "./Auth.css";
import { PASSWORD_HELP_TEXT, validatePassword } from "../../utils/passwordPolicy";
import { USERNAME_HELP_TEXT, validateUsername } from "../../utils/usernamePolicy";
import { validateEmail } from "../../utils/emailPolicy";

// Define API URL as a constant
const API_URL = process.env.REACT_APP_API_URL || "/api";

const Signup = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);
  const [resendError, setResendError] = useState(null);
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState("");

  // Promjena vrijednosti inputa
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Slanje obrasca
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setPasswordError(null);
    setShowResend(false);
    setResendMessage(null);
    setResendError(null);

    if (formData.password !== confirmPassword) {
      setPasswordError("Lozinke se ne podudaraju.");
      return;
    }

    const usernamePolicyError = validateUsername(formData.username);
    if (usernamePolicyError) {
      setError(usernamePolicyError);
      return;
    }

    const emailPolicyError = validateEmail(formData.email);
    if (emailPolicyError) {
      setError(emailPolicyError);
      return;
    }

    const passwordPolicyError = validatePassword(formData.password, {
      username: formData.username,
      email: formData.email,
    });
    if (passwordPolicyError) {
      setPasswordError(passwordPolicyError);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      const data = await response.json();

      if (response.ok) {
        // Ako se pokreće lokalno, dodajemo placeholder obavijest
        const finalMessage =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1"
            ? "Korisnik uspješno registrovan. (Lokalno: Email verifikacija trenutno nije aktivirana.)"
            : "Korisnik uspješno registrovan. Potvrdite email putem poslanog linka (provjerite i spam folder). Nakon potvrde automatski ćete biti prijavljeni.";

        setMessage(finalMessage);
        setShowResend(true);
        // Remove automatic redirect to allow users to resend verification
        // setTimeout(() => navigate("/login"), 15000);
      } else {
        // Check if the error is about existing user/email
        if (data.error && data.error.includes("već zauzeti")) {
          setError(data.error);
          setShowResend(true);
          setResendEmail(formData.email); // Pre-fill the email for resend
        } else {
          setError(data.error || data.message || "Registracija nije uspjela.");
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError("Došlo je do greške. Molimo pokušajte ponovo.");
    }
  };

  // Resend verification email
  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    try {
      const response = await fetch(`${API_URL}/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage(data.message || "Novi verifikacioni email je poslan!");
      } else {
        setResendError(data.error || data.message || "Greška pri slanju verifikacionog emaila.");
      }
    } catch (err) {
      console.error('Resend error:', err);
      setResendError("Došlo je do greške. Molimo pokušajte ponovo.");
    } finally {
      setResendLoading(false);
    }
  };

  // Resend verification email with custom email
  const handleResendVerificationCustom = async () => {
    if (!resendEmail) {
      setResendError("Molimo unesite email adresu.");
      return;
    }

    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    try {
      const response = await fetch(`${API_URL}/resend-verification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({ email: resendEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage(data.message || "Novi verifikacioni email je poslan!");
        setShowResendForm(false);
      } else {
        setResendError(data.error || data.message || "Greška pri slanju verifikacionog emaila.");
      }
    } catch (err) {
      console.error('Resend error:', err);
      setResendError("Došlo je do greške. Molimo pokušajte ponovo.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <LogoTray />
      <div className="auth-container">
        <div className="auth-box">
        <div className="tech-border">
          <div className="auth-content">
            <h2 className="text-center mb-4">Registracija</h2>
            {message && <div className="alert alert-success text-center">{message}</div>}
            {error && <div className="alert alert-danger text-center">{error}</div>}
            {passwordError && <div className="alert alert-danger text-center">{passwordError}</div>}
            {resendMessage && <div className="alert alert-success text-center">{resendMessage}</div>}
            {resendError && <div className="alert alert-danger text-center">{resendError}</div>}
            
            {!showResend ? (
              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group mb-3">
                  <label>Korisničko ime</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    required
                    title={USERNAME_HELP_TEXT}
                    autoComplete="username"
                    className="form-control tech-input"
                    placeholder="Unesite korisničko ime"
                  />
                  <small className="form-text">{USERNAME_HELP_TEXT}</small>
                </div>
                <div className="form-group mb-3">
                  <label>Email adresa</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-control tech-input"
                    placeholder="Unesite email adresu"
                  />
                </div>
                <div className="form-group mb-3">
                  <label>Lozinka</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      autoComplete="new-password"
                      className="form-control tech-input"
                      placeholder="Unesite lozinku"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  <small className="form-text">{PASSWORD_HELP_TEXT}</small>
                </div>
                <div className="form-group mb-4">
                  <label>Potvrda lozinke</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(null); }}
                      required
                      autoComplete="new-password"
                      className="form-control tech-input"
                      placeholder="Ponovite lozinku"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary w-100 tech-button">
                  Registruj se
                </button>
              </form>
            ) : (
              <div className="text-center">
                <p className="mb-3">Niste primili verifikacioni email?</p>
                <button 
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="btn btn-outline-primary tech-button mb-3"
                >
                  {resendLoading ? "Slanje..." : "Pošalji ponovo verifikacioni email"}
                </button>
                <div className="mt-3">
                  <a href="/login" className="tech-link">
                    Idi na prijavu
                  </a>
                </div>
              </div>
            )}
            
            {!showResend && (
              <p className="text-center mt-3">
                Već imate račun?{" "}
                <a href="/login" className="tech-link">
                  Prijavite se ovdje
                </a>
              </p>
            )}

            {/* Always visible resend option */}
            <div className="text-center mt-4">
              <p className="mb-2">Niste primili verifikacioni email?</p>
              {!showResendForm ? (
                <button 
                  onClick={() => setShowResendForm(true)}
                  className="btn btn-link tech-link"
                >
                  Ponovo pošalji verifikacioni link
                </button>
              ) : (
                <div className="mt-3">
                  <div className="form-group mb-3">
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      className="form-control tech-input"
                      placeholder="Unesite vašu email adresu"
                    />
                  </div>
                  <button 
                    onClick={handleResendVerificationCustom}
                    disabled={resendLoading}
                    className="btn btn-outline-primary tech-button mb-2"
                  >
                    {resendLoading ? "Slanje..." : "Pošalji verifikacioni email"}
                  </button>
                  <div>
                    <button 
                      onClick={() => {
                        setShowResendForm(false);
                        setResendEmail("");
                        setResendMessage(null);
                        setResendError(null);
                      }}
                      className="btn btn-link tech-link"
                    >
                      Odustani
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Signup;
